import {
    AgentState,
    AgentStates,
    MediaType,
    MediaTypes,
} from '@dxp/core/types';
import {
    aggregateWorkItemsPerMediaType,
    calculateUserPresence,
    calculateWithPercentage,
    convertToMediaTypeRecord,
} from '@dxp/core/helpers';
import { IPresence } from '../presence/presence';
import { IQueueActivity } from '../queue/queue-activity';
import { IUserRead } from '../rbac/user';
import { IWorkItemRelation } from '../workitem/history';
import { IHandledCountKpi, IWorkItemKpis } from '../workitem/workitem';
import { NBU_ID } from '../business-unit/business-unit';
import { IValueWithMetric } from '../metrics/metric';
import { IKeyValuePair } from '../application/key-value';
import { IBlenderAgent } from './agent-state';

export interface IActiveAgentDirectoryEvent {
    type: 'selection' | 'voice' | 'email' | 'custom';
    item: IBlenderAgent;
}

export interface IAgentDirectoryEvent {
    type: 'selection' | 'voice' | 'email' | 'custom';
    item: IUserRead;
}

export interface IAgentRelations {
    channels: IWorkItemRelation[];
    queues: IWorkItemRelation[];
    completionCodes: IWorkItemRelation[];
}

export interface IAgentState {
    agentId: string;
    agentName: string;
    state: AgentState;
    stateSince: Date;
    breakName: string;
    businessUnitIds: string[];
    breakExpiresAt?: Date;
    pendingState?: AgentState;
    pendingBreakName?: string;
}

export interface IAgentReport {
    id: string;
    name: string;
    tenantId: string;
    externalTaskCountPerBusinessUnit: Record<string, number>;
    avgExternalTaskTimeInSecondsPerBusinessUnit: Record<string, number>;
    workItemKpisPerDispositionCode: IKeyValuePair<IHandledCountKpi>[];
    secondsSpentPerState: IKeyValuePair<number>[];
    secondsSpentPerBreakName: IKeyValuePair<number>[];
    loggedInAt?: Date;
    loggedOutAt?: Date;
    handledInboundVoiceTaskCountPerBusinessUnit: Record<string, number>;
    handledInboundChatTaskCountPerBusinessUnit: Record<string, number>;
    handledInboundEmailTaskCountPerBusinessUnit: Record<string, number>;
    avgInboundVoiceHandlingTimeInSecondsPerBusinessUnit: Record<string, number>;
    avgInboundChatHandlingTimeInSecondsPerBusinessUnit: Record<string, number>;
    avgInboundEmailHandlingTimeInSecondsPerBusinessUnit: Record<string, number>;
    missedInboundVoiceTaskCountPerBusinessUnit: Record<string, number>;
    missedInboundEmailTaskCountPerBusinessUnit: Record<string, number>;
    missedInboundChatTaskCountPerBusinessUnit: Record<string, number>;
    rejectedInboundVoiceTaskCountPerBusinessUnit: Record<string, number>;
    rejectedInboundEmailTaskCountPerBusinessUnit: Record<string, number>;
    rejectedInboundChatTaskCountPerBusinessUnit: Record<string, number>;
    handledOutboundVoiceTaskCountPerBusinessUnit: Record<string, number>;
    handledOutboundChatTaskCountPerBusinessUnit: Record<string, number>;
    handledOutboundEmailTaskCountPerBusinessUnit: Record<string, number>;
    avgOutboundVoiceHandlingTimeInSecondsPerBusinessUnit: Record<
        string,
        number
    >;
    avgOutboundChatHandlingTimeInSecondsPerBusinessUnit: Record<string, number>;
    avgOutboundEmailHandlingTimeInSecondsPerBusinessUnit: Record<
        string,
        number
    >;
}

export interface IAgent {
    id: string;
    name: string;
    emailAddress?: string;
    phoneNumber?: string;
    report?: IAgentReport;
    hasActivity: boolean;
    businessUnits: string[];
    state?: IAgentState;
    status: { state: AgentState; since: Date; breakName: string };
    presence: IPresence;
    statistics: IAgentStatistics;
}

export interface IAgentStatistics {
    totalTimeLoggedIn: number;
    timeSpentInAgentStates: Record<
        AgentState,
        { value: number; percentage: number }
    >;
    timeSpentInBreakNames: Record<
        string,
        { value: number; percentage: number }
    >;
    workItemDispositionCodes: Record<
        string,
        { value: number; percentage: number }
    >;
    workItemsPerMediaType: Record<
        MediaType,
        {
            split: number;
            handledCount: number;
            missedCount: number;
            averageHandlingTimeInSeconds: IValueWithMetric;
            ratio: number;
        }
    >;
    tasks: {
        handled: number;
        missed: number;
        ratio: number;
        averageHandlingTimeInSeconds: IValueWithMetric;
    };
    timeSpentInWrap: { value: number; percentage: number };
}

export class Agent implements IAgent {
    readonly id: string;
    readonly #report?: IAgentReport;
    readonly #state?: IAgentState;
    readonly #activity: IQueueActivity[];

    constructor(
        id: string,
        report?: IAgentReport,
        state?: IAgentState,
        activity?: IQueueActivity[],
    ) {
        this.id = id;
        this.#report = report;
        this.#state = state;
        this.#activity = activity ?? [];
    }

    get name(): string {
        return this.#report?.name ?? this.#state?.agentName ?? '';
    }

    get status(): {
        state: AgentState;
        since: Date;
        breakName: string;
        expiresAt?: Date;
        pendingState?: AgentState;
        pendingBreakName?: string;
    } {
        return this.#state
            ? {
                  state: this.#state.state,
                  since: this.#state.stateSince,
                  breakName: this.#state.breakName ?? '',
                  expiresAt: this.#state.breakExpiresAt,
                  pendingState: this.#state.pendingState,
                  pendingBreakName: this.#state.pendingBreakName,
              }
            : {
                  state: 'LoggedOut',
                  since: new Date(),
                  breakName: '',
              };
    }

    get businessUnits(): string[] {
        return [...(this.#state?.businessUnitIds ?? []), NBU_ID];
    }

    get hasActivity(): boolean {
        return (
            (this.#report !== undefined && this.#report.name.length > 0) ||
            (this.#state?.state !== 'LoggedOut' &&
                (this.state?.agentName ?? '').length > 0)
        );
    }

    get report(): IAgentReport | undefined {
        return this.#report;
    }

    get state(): IAgentState | undefined {
        return this.#state;
    }

    get presence(): IPresence {
        return calculateUserPresence(
            this.status.state,
            this.status.since,
            this.status.breakName,
            this.status.expiresAt,
            this.status.pendingState,
            this.status.pendingBreakName,
        );
    }

    get statistics(): IAgentStatistics {
        const timeSpentInWrap = this.#activity.reduce(
            (acc, act) => acc + act.wrapTimeDuration,
            0,
        );
        const timeSpentInBusy =
            this.#report?.secondsSpentPerState.find(pair => pair.key === 'Busy')
                ?.value ?? 0;

        const workItemKpisPerMediaType = this.#workItemStatsPerMediaType();

        return {
            timeSpentInAgentStates: calculateWithPercentage(
                AgentStates.filter(
                    as => as !== 'LoggedOut',
                ) as readonly string[],
                this.#report?.secondsSpentPerState ?? [],
                value => value,
            ),
            totalTimeLoggedIn:
                this.#report?.secondsSpentPerState
                    .filter(pair => pair.key !== 'LoggedOut')
                    .reduce((total, pair) => total + pair.value, 0) ?? 0,
            timeSpentInBreakNames: calculateWithPercentage(
                this.#report?.secondsSpentPerBreakName.map(kvp => kvp.key) ??
                    [],
                this.#report?.secondsSpentPerBreakName ?? [],
                value => value,
            ),
            workItemDispositionCodes: calculateWithPercentage(
                this.#report?.workItemKpisPerDispositionCode.map(
                    kvp => kvp.key,
                ) ?? [],
                this.#report?.workItemKpisPerDispositionCode ?? [],
                item => item.handledCount,
            ),
            workItemsPerMediaType: convertToMediaTypeRecord(
                workItemKpisPerMediaType.filter(w =>
                    MediaTypes.includes(w.key as MediaType),
                ) ?? [],
            ),
            tasks: aggregateWorkItemsPerMediaType(
                workItemKpisPerMediaType.filter(w =>
                    MediaTypes.includes(w.key as MediaType),
                ) ?? [],
            ),
            timeSpentInWrap: {
                value: timeSpentInWrap,
                percentage:
                    timeSpentInBusy > 0
                        ? Math.round((timeSpentInWrap / timeSpentInBusy) * 100)
                        : 0,
            },
        };
    }

    #workItemStatsPerMediaType(): IKeyValuePair<IWorkItemKpis>[] {
        const voiceHandlingStats: IWorkItemKpis =
            this.#createWorkItemHandlingStats(
                [
                    ...Object.values(
                        this.#report
                            ?.handledInboundVoiceTaskCountPerBusinessUnit ?? {},
                    ),
                    ...Object.values(
                        this.report
                            ?.handledOutboundVoiceTaskCountPerBusinessUnit ??
                            {},
                    ),
                ],
                [
                    ...Object.values(
                        this.report
                            ?.missedInboundVoiceTaskCountPerBusinessUnit ?? {},
                    ),
                    ...Object.values(
                        this.report
                            ?.rejectedInboundVoiceTaskCountPerBusinessUnit ??
                            {},
                    ),
                ],
                [
                    ...Object.values(
                        this.report
                            ?.avgInboundVoiceHandlingTimeInSecondsPerBusinessUnit ??
                            {},
                    ),
                    ...Object.values(
                        this.report
                            ?.avgOutboundVoiceHandlingTimeInSecondsPerBusinessUnit ??
                            {},
                    ),
                ],
            );

        const emailHandlingStats: IWorkItemKpis =
            this.#createWorkItemHandlingStats(
                [
                    ...Object.values(
                        this.report
                            ?.handledInboundEmailTaskCountPerBusinessUnit ?? {},
                    ),
                    ...Object.values(
                        this.report
                            ?.handledOutboundEmailTaskCountPerBusinessUnit ??
                            {},
                    ),
                ],
                [
                    ...Object.values(
                        this.report
                            ?.missedInboundEmailTaskCountPerBusinessUnit ?? {},
                    ),
                    ...Object.values(
                        this.report
                            ?.rejectedInboundEmailTaskCountPerBusinessUnit ??
                            {},
                    ),
                ],
                [
                    ...Object.values(
                        this.report
                            ?.avgInboundEmailHandlingTimeInSecondsPerBusinessUnit ??
                            {},
                    ),
                    ...Object.values(
                        this.report
                            ?.avgOutboundEmailHandlingTimeInSecondsPerBusinessUnit ??
                            {},
                    ),
                ],
            );

        const chatHandlingStats: IWorkItemKpis =
            this.#createWorkItemHandlingStats(
                [
                    ...Object.values(
                        this.report
                            ?.handledInboundChatTaskCountPerBusinessUnit ?? {},
                    ),
                    ...Object.values(
                        this.report
                            ?.handledOutboundChatTaskCountPerBusinessUnit ?? {},
                    ),
                ],
                [
                    ...Object.values(
                        this.report
                            ?.missedInboundChatTaskCountPerBusinessUnit ?? {},
                    ),
                    ...Object.values(
                        this.report
                            ?.rejectedInboundChatTaskCountPerBusinessUnit ?? {},
                    ),
                ],
                [
                    ...Object.values(
                        this.report
                            ?.avgInboundChatHandlingTimeInSecondsPerBusinessUnit ??
                            {},
                    ),
                    ...Object.values(
                        this.report
                            ?.avgOutboundChatHandlingTimeInSecondsPerBusinessUnit ??
                            {},
                    ),
                ],
            );
        const externalHandlingStats: IWorkItemKpis =
            this.#createWorkItemHandlingStats(
                Object.values(
                    this.report?.externalTaskCountPerBusinessUnit ?? {},
                ),
                [],
                Object.values(
                    this.report?.avgExternalTaskTimeInSecondsPerBusinessUnit ??
                        {},
                ),
            );

        return [
            { key: 'Voice', value: voiceHandlingStats },
            { key: 'Email', value: emailHandlingStats },
            { key: 'Webchat', value: chatHandlingStats },
            { key: 'Messaging', value: externalHandlingStats },
        ];
    }

    #createWorkItemHandlingStats(
        handled: number[],
        missed: number[],
        avgHandlingTime: number[],
    ): IWorkItemKpis {
        return {
            handledCount: handled.reduce((sum, count) => sum + count, 0),
            missedCount: missed.reduce((sum, count) => sum + count, 0),
            averageHandlingTimeInSeconds:
                avgHandlingTime.reduce((sum, time) => sum + time, 0) /
                Math.max(1, handled.length),
        };
    }
}
